import React, { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Egg, Sparkles, Wheat, Trophy, Store, Handshake, Plus, Trash2, PackageCheck, PackageX, Coins, TrendingUp, Timer, ShoppingBag, X, CalendarDays, Info, Home } from "lucide-react";
import BarnBoard from "@/components/BarnBoard";
import { toast } from "sonner";
import { format, parseISO, differenceInSeconds } from "date-fns";
import { mdy, mdyDow, mdTime, mdyTime } from "@/lib/dateFmt";
import { useAuth } from "@/context/AuthContext";
import LevelBadge from "@/components/LevelBadge";
import FarmScene from "@/components/FarmScene";

const TIER_STYLE = {
  golden:  { label: "GOLDEN · Today/Tomorrow", cls: "bg-amber-100 border-amber-300 text-amber-900",       ring: "ring-amber-300" },
  urgent:  { label: "Urgent · 2–3 days",       cls: "bg-orange-50 border-orange-200 text-orange-900",     ring: "ring-orange-200" },
  soon:    { label: "Soon · This week",        cls: "bg-lime-50 border-lime-200 text-lime-900",           ring: "ring-lime-200" },
  distant: { label: "Far out · Next week+",    cls: "bg-emerald-50 border-emerald-200 text-emerald-900",  ring: "ring-emerald-200" },
};

function useCountdown(targetIso) {
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (!targetIso) { setRemaining(null); return; }
    const tick = () => setRemaining(Math.max(0, differenceInSeconds(parseISO(targetIso), new Date())));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  return remaining;
}
const fmtCountdown = (s) => {
  if (s == null) return "";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return `${h}h ${String(m).padStart(2, "0")}m ${String(ss).padStart(2, "0")}s`;
};

export default function ShiftFarmPage({ facilityId, facilityLabel }) {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const isEmployee = user?.role === "employee";

  const [tab, setTab] = useState("board");
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [tradeAds, setTradeAds] = useState([]);
  const [store, setStore] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [mySchedule, setMySchedule] = useState([]);
  const prevLevelRef = useRef(null);

  const reload = async () => {
    try {
      const [meR, brdR, ldrR, adsR, strR, redR] = await Promise.all([
        api.get("/game/me"),
        api.get(`/game/board?facility_id=${facilityId}`),
        api.get(`/game/leaderboard?facility_id=${facilityId}`),
        api.get(`/game/trade-ads?facility_id=${facilityId}`),
        api.get(`/game/store?facility_id=${facilityId}`),
        api.get(`/game/redemptions?facility_id=${facilityId}`),
      ]);
      setMe(meR.data);
      // Detect level-up (compared to previously seen level) and celebrate.
      const newLvl  = meR.data?.level?.level;
      const newLbl  = meR.data?.level?.label || "";
      const prevLvl = prevLevelRef.current;
      if (typeof newLvl === "number" && typeof prevLvl === "number" && newLvl > prevLvl) {
        // Confetti burst from bottom-center — feels like a harvest pop.
        const fire = (opts) =>
          confetti({ startVelocity: 55, spread: 90, ticks: 200,
                     origin: { x: 0.5, y: 0.85 },
                     colors: ["#f59e0b", "#22c55e", "#3b82f6", "#ec4899", "#a855f7"], ...opts });
        fire({ particleCount: 90 });
        setTimeout(() => fire({ particleCount: 60, origin: { x: 0.2, y: 0.9 } }), 220);
        setTimeout(() => fire({ particleCount: 60, origin: { x: 0.8, y: 0.9 } }), 380);
        toast.success(`Level up! L${newLvl} · ${newLbl}`, { duration: 5000 });
      }
      if (typeof newLvl === "number") prevLevelRef.current = newLvl;
      setBoard(brdR.data || []);
      setLeaders(ldrR.data || []);
      setTradeAds(adsR.data || []);
      setStore(strR.data || []);
      setRedemptions(redR.data || []);
    } catch (e) { console.warn("ShiftFarm reload failed:", e); }
    if (isEmployee) {
      try {
        const now = new Date(); const anchor = now.toISOString().slice(0, 10);
        // Fetch my upcoming assignments through /me/schedule (we'll reuse the schedule endpoint's employee-side view when composing a trade ad)
      } catch (e) { console.warn("ShiftFarm employee reload failed:", e); }
    }
  };
  useEffect(() => { if (facilityId) reload(); /* eslint-disable-next-line */ }, [facilityId]);

  const nextEgg = useCountdown(me?.next_egg_at);
  const canClaimEggs = me && (!me.next_egg_at || nextEgg === 0);
  const claimEggs = async () => {
    try {
      const { data } = await api.post("/game/claim-eggs");
      toast.success(`+${data.granted.coins} coins · +${data.granted.xp} XP`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Not ready");
    }
  };

  const plant = async (s) => {
    try {
      await api.post(`/game/plant/${encodeURIComponent(s.shift_code)}?date=${s.date}&facility_id=${facilityId}`);
      toast.success(`Planted! Manager will confirm — worth ${s.reward.coins} coins + ${s.reward.xp} XP`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't plant");
    }
  };

  const goldenSeeds = useMemo(() => board.filter(s => s.reward?.tier === "golden"), [board]);
  const otherSeeds = useMemo(() => board.filter(s => s.reward?.tier !== "golden"), [board]);

  return (
    <div className="space-y-4 pb-4" data-testid="shift-farm">
      {/* Painted farm hero — level-dependent illustration + name / XP / coins / eggs overlay */}
      <FarmScene
        me={me}
        facilityLabel={facilityLabel}
        canClaimEggs={canClaimEggs}
        nextEgg={nextEgg}
        onClaimEggs={claimEggs}
      />

      {/* Tabs — horizontally scrollable on mobile */}
      <div className="flex gap-2 bg-slate-100 rounded-full p-1 text-sm overflow-x-auto no-scrollbar" data-testid="farm-tabs">
        {[
          { id: "board",       label: "Barn",        icon: Home },
          { id: "trades",      label: "Trades",      icon: Handshake },
          { id: "leaderboard", label: "Top",         icon: Trophy },
          { id: "store",       label: "Store",       icon: ShoppingBag },
          ...(isManager ? [{ id: "manage-store", label: "Manage", icon: Store }] : []),
          ...(isAdmin    ? [{ id: "ladder",       label: "Ladder", icon: TrendingUp }] : []),
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`farm-tab-${t.id}`}
            className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap shrink-0 ${
              tab === t.id ? "bg-white shadow font-semibold text-slate-800" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* Barn tab — 4-week paycheck-period view with pickable open shifts */}
      {tab === "board" && (
        <BarnBoard facilityId={facilityId} onPlanted={reload} />
      )}

      {tab === "trades" && (
        <TradeBoard facilityId={facilityId} ads={tradeAds} onChange={reload} me={user} isEmployee={isEmployee} />
      )}

      {tab === "leaderboard" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="farm-leaderboard">
          <div className="flex items-center gap-2 font-display font-semibold mb-2">
            <Trophy size={16} className="text-amber-600" /> Top farmers this facility
          </div>
          {leaders.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No farmers on the board yet — pick up a shift to break the seal.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {leaders.map((l, i) => (
                <div key={l.user_id} className="flex items-center gap-3 py-2" data-testid={`leader-row-${i+1}`}>
                  <div className={`w-7 h-7 grid place-items-center rounded-full font-bold text-sm ${
                    i === 0 ? "bg-amber-200 text-amber-900" :
                    i === 1 ? "bg-slate-200 text-slate-700" :
                    i === 2 ? "bg-orange-200 text-orange-900" :
                              "bg-slate-100 text-slate-500"
                  }`}>{i + 1}</div>
                  <LevelBadge level={l.level} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="text-[11px] text-slate-500">{l.level.label} · {l.xp} XP</div>
                  </div>
                  <div className="text-sm font-semibold text-amber-700 inline-flex items-center gap-1"><Coins size={12} /> {l.coins}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "store" && (
        <StoreTab items={store} onBuy={async (item) => {
          if (!window.confirm(`Redeem ${item.name} for ${item.cost} coins? Your manager will fulfill it.`)) return;
          try {
            await api.post(`/game/store/${item.id}/purchase`);
            toast.success("Redemption submitted — waiting on manager approval");
            reload();
          } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't redeem");
          }
        }} coins={me?.coins ?? 0} redemptions={redemptions} />
      )}

      {tab === "manage-store" && isManager && (
        <ManageStoreTab facilityId={facilityId} items={store} onChange={reload}
          redemptions={redemptions.filter(r => r.status === "pending")}
          onDecide={async (id, decision) => {
            try {
              await api.patch(`/game/redemptions/${id}`, { decision, manager_note: "" });
              toast.success(decision === "denied" ? "Denied and refunded" : "Marked " + decision);
              reload();
            } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
          }}
        />
      )}

      {tab === "ladder" && isAdmin && <LadderTab />}
    </div>
  );
}

function LadderTab() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [zoom, setZoom] = React.useState(null); // { level, tier, image, unlock } | null

  React.useEffect(() => {
    let alive = true;
    api.get("/game/level-ladder").then(({ data }) => { if (alive) { setData(data); setLoading(false); } })
       .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading the level ladder…</div>;
  }
  if (!data) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-red-600">Could not load the ladder.</div>;
  }

  // Group levels by tier for cleaner scrolling
  const groups = [];
  data.levels.forEach((l) => {
    const g = groups[groups.length - 1];
    if (!g || g.tier !== l.tier_name) groups.push({ tier: l.tier_name, image: l.milestone_image, levels: [l] });
    else g.levels.push(l);
  });

  return (
    <div className="space-y-4" data-testid="farm-ladder">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-amber-50 to-white p-4">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={16} className="text-amber-700" />
          <div className="font-display font-black text-lg text-slate-900">Progression Ladder</div>
          <span className="text-[10px] uppercase tracking-widest font-bold rounded-md border border-red-300 bg-red-50 text-red-700 px-1.5 py-0.5">Admin</span>
        </div>
        <div className="text-sm text-slate-600 max-w-3xl">
          {data.max_level} levels total. Curve is quadratic through L20 for satisfying early climbs, then linear so a
          picker still has room to grow after years. Assumptions: casual player = 4 shifts/mo (~100 XP), heavy player
          = 12 shifts/mo (~300 XP), avg pickup reward = 25 XP.
        </div>
      </div>

      <div className="space-y-6">
        {groups.map((g) => (
          <div key={g.tier} className="rounded-2xl border-2 border-amber-200/60 bg-white overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-stretch">
              <button
                type="button"
                onClick={() => setZoom({ level: g.levels[0].level, tier: g.tier, image: g.image, unlock: g.levels[0].unlock })}
                className="relative md:w-[38%] shrink-0 aspect-[16/10] md:aspect-auto bg-slate-100 overflow-hidden group"
                data-testid={`ladder-tier-${g.tier.replace(/\s+/g,'-').toLowerCase()}`}
              >
                <img src={g.image} alt={g.tier} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                <div className="absolute top-2 left-2 rounded-full bg-white/90 backdrop-blur px-2 py-0.5 text-[10px] uppercase tracking-widest font-black text-slate-800">
                  {g.tier}
                </div>
                <div className="absolute bottom-2 left-2 right-2 flex justify-between items-end">
                  <div className="rounded-md bg-black/60 text-white px-2 py-0.5 text-[11px] font-semibold">
                    L{g.levels[0].level}–L{g.levels[g.levels.length - 1].level}
                  </div>
                  <div className="rounded-md bg-white/95 text-slate-900 px-2 py-0.5 text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                    Click to preview
                  </div>
                </div>
              </button>

              <div className="flex-1 p-3 md:p-4">
                <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
                  {g.levels.map((l) => (
                    <div key={l.level} className="rounded-xl border border-slate-200 p-2.5 hover:border-amber-300 hover:bg-amber-50/40 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="inline-flex items-center gap-1 font-bold text-slate-900">
                          <span className="text-[10px] font-black text-amber-800 uppercase tracking-widest">L</span>
                          <span className="text-lg tabular-nums">{l.level}</span>
                        </div>
                        <span className="tabular-nums text-[10px] font-semibold text-slate-500">{l.xp_required.toLocaleString()} XP</span>
                      </div>
                      <div className="text-[12px] text-slate-800 leading-tight mt-0.5 truncate" title={l.unlock}>{l.unlock}</div>
                      <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-500 tabular-nums">
                        <span title="Months to reach as a casual picker (4 shifts/mo)">
                          🐢 {l.months_casual}mo
                        </span>
                        <span className="text-slate-300">·</span>
                        <span title="Months to reach as a heavy picker (12 shifts/mo)">
                          🚜 {l.months_heavy}mo
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {zoom && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setZoom(null)}
          data-testid="ladder-zoom"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <img src={zoom.image} alt={zoom.tier} className="w-full aspect-video object-cover" />
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest font-black text-amber-800">Tier {zoom.tier}</div>
              <div className="font-display font-black text-2xl text-slate-900 mt-0.5">L{zoom.level}+ · {zoom.unlock}</div>
              <div className="text-sm text-slate-500 mt-1">
                This is what the farm illustration looks like once a player reaches the {zoom.tier} tier. Each of the 10 levels inside this tier adds a themed unlock line (see the cards on the ladder).
              </div>
              <div className="mt-3 text-right">
                <button onClick={() => setZoom(null)} className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm font-semibold" data-testid="ladder-zoom-close">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SeedCard({ s, onPlant }) {
  const style = TIER_STYLE[s.reward?.tier] || TIER_STYLE.distant;
  return (
    <div className={`rounded-xl border p-3 ${style.cls}`} data-testid={`seed-${s.key}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{style.label}</div>
          <div className="font-display font-bold text-lg mt-0.5">{s.shift_code} · {s.label}</div>
          <div className="text-xs opacity-80">{mdyDow(s.date)} · {s.start ?? "?"}–{s.end ?? "?"}</div>
          {s.department && <div className="text-[11px] mt-1 opacity-70">{s.department}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest font-bold">Reward</div>
          <div className="font-bold tabular-nums">+{s.reward?.coins ?? 0} 🪙</div>
          <div className="text-[10px] opacity-80">+{s.reward?.xp ?? 0} XP</div>
        </div>
      </div>
      <Button size="sm" onClick={() => onPlant(s)} className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700" data-testid={`plant-${s.key}`}>
        <Wheat size={12} className="mr-1" /> Plant this seed
      </Button>
    </div>
  );
}

function TradeBoard({ facilityId, ads, onChange, me, isEmployee }) {
  const [showNew, setShowNew] = useState(false);
  return (
    <div className="relative max-w-5xl mx-auto" data-testid="trade-board">
      {/* Roadside-market awning frame */}
      <div className="relative rounded-t-[28px] overflow-visible shadow-[0_18px_45px_-20px_rgba(0,0,0,0.35)]">
        {/* Striped umbrella / awning roof — repeat pattern scales naturally so it doesn't stretch on wide screens */}
        <div className="relative h-12 sm:h-14"
             style={{
               background:
                 "repeating-linear-gradient(90deg, #e11d48 0 34px, #fefefe 34px 68px)",
               clipPath: "polygon(0 100%, 3% 30%, 6% 100%, 9% 30%, 12% 100%, 15% 30%, 18% 100%, 21% 30%, 24% 100%, 27% 30%, 30% 100%, 33% 30%, 36% 100%, 39% 30%, 42% 100%, 45% 30%, 48% 100%, 51% 30%, 54% 100%, 57% 30%, 60% 100%, 63% 30%, 66% 100%, 69% 30%, 72% 100%, 75% 30%, 78% 100%, 81% 30%, 84% 100%, 87% 30%, 90% 100%, 93% 30%, 96% 100%, 100% 100%)",
             }}
        >
          <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/25" />
        </div>
        {/* Pennant flags */}
        <div className="absolute left-4 right-4 top-1 flex items-center gap-1 opacity-90 pointer-events-none">
          {Array.from({length: 18}).map((_, i) => (
            <div key={i} className="w-2 h-3" style={{ background: i % 2 ? "#fef3c7" : "#fca5a5", clipPath: "polygon(0 0, 100% 0, 50% 100%)" }} />
          ))}
        </div>

        {/* Market booth body — wooden crates vibe */}
        <div className="relative bg-[#f8ecd6] border-x-[6px] border-b-[6px] border-[#a06a2e] p-3 sm:p-4 rounded-b-2xl"
             style={{
               backgroundImage:
                 "repeating-linear-gradient(0deg, rgba(160,106,46,0.10) 0 1px, transparent 1px 22px)",
             }}
        >
          {/* Booth signboard */}
          <div className="rounded-xl bg-white border-4 border-[#a06a2e] p-3 sm:p-4 shadow-inner">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-display font-semibold flex items-center gap-2 text-[#7a3a20]">
                <Handshake size={16} className="text-rose-600" />
                <span className="font-black text-lg">Trade Market</span>
                <span className="text-xs font-normal text-[#a06a2e]">Only shifts 3+ days out · Same pay period · Managers approve</span>
              </div>
              {isEmployee && (
                <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => setShowNew(true)} data-testid="trade-new-btn">
                  <Plus size={12} className="mr-1" /> Post a trade ad
                </Button>
              )}
            </div>
          </div>

          {ads.length === 0 ? (
            <div className="mt-3 rounded-xl border-2 border-dashed border-[#a06a2e]/50 bg-white/70 p-8 text-center text-[#7a3a20] text-sm">
              The stalls are empty — be the first to post a trade and earn +{150} coins if it clears.
            </div>
          ) : (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {ads.map(ad => (
                <TradeAdCard key={ad.id} ad={ad} facilityId={facilityId} me={me} onChange={onChange} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showNew && <NewTradeAdDialog open={showNew} onClose={() => setShowNew(false)} facilityId={facilityId} onSaved={onChange} />}
    </div>
  );
}

function TradeAdCard({ ad, facilityId, me, onChange }) {
  const isMine = ad.owner_id === me?.id;
  const [pickingDate, setPickingDate] = useState("");
  const accept = async () => {
    if (!pickingDate) { toast.error("Pick a return date"); return; }
    try {
      await api.post(`/game/trade-ads/${ad.id}/accept`, { return_date: pickingDate });
      toast.success("Trade submitted — manager will decide");
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't accept"); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this trade ad?")) return;
    try { await api.delete(`/game/trade-ads/${ad.id}`); toast.success("Ad removed"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`trade-ad-${ad.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Giving away</div>
          <div className="font-display font-bold">{ad.my_shift_code || "?"} on {mdyDow(ad.my_date)}</div>
          <div className="text-xs text-slate-500">by {ad.owner_name}</div>
        </div>
        {isMine && (
          <button onClick={cancel} className="text-slate-400 hover:text-red-600" title="Cancel my ad" data-testid={`cancel-ad-${ad.id}`}>
            <X size={14} />
          </button>
        )}
      </div>
      <div className="mt-2 border-t border-slate-100 pt-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Willing to take back</div>
        <div className="flex flex-wrap gap-1 mt-1">
          {(ad.willing_dates || []).map(d => (
            <label key={d} className={`text-[11px] px-2 py-0.5 rounded-md border cursor-pointer ${
              pickingDate === d ? "bg-blue-100 border-blue-400 text-blue-800" : "bg-slate-50 border-slate-200 text-slate-700"
            }`}>
              <input type="radio" name={`ad-${ad.id}`} value={d} className="hidden"
                checked={pickingDate === d} onChange={() => setPickingDate(d)}
                disabled={isMine} data-testid={`ad-${ad.id}-date-${d}`} />
              {mdyDow(d)}
            </label>
          ))}
        </div>
      </div>
      {!isMine && (
        <Button size="sm" onClick={accept} disabled={!pickingDate}
          className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700"
          data-testid={`accept-ad-${ad.id}`}>
          <Handshake size={12} className="mr-1" /> Accept trade (+{150} coins if approved)
        </Button>
      )}
    </div>
  );
}

function formatPretty(iso) {
  try { return mdyDow(iso); } catch { return iso; }
}

function NewTradeAdDialog({ open, onClose, facilityId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [myDate, setMyDate] = useState("");
  const [willingDates, setWillingDates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !facilityId) return;
    setLoading(true);
    setMyDate(""); setWillingDates([]);
    api.get(`/game/my-trade-dates?facility_id=${facilityId}`)
      .then(r => setPeriods(r.data?.pay_periods || []))
      .catch(() => setPeriods([]))
      .finally(() => setLoading(false));
  }, [open, facilityId]);

  // Which pay period does the selected my_date belong to? Willing dates must be in the same one.
  const activePeriod = useMemo(
    () => periods.find(p => (p.my_shifts || []).includes(myDate)),
    [periods, myDate]
  );
  const offDaysForSelected = activePeriod?.my_off_days || [];

  // When my_date changes, drop willing dates outside the new pay period.
  useEffect(() => {
    if (!activePeriod) { if (willingDates.length) setWillingDates([]); return; }
    setWillingDates(prev => prev.filter(d => offDaysForSelected.includes(d)));
    // eslint-disable-next-line
  }, [myDate]);

  const toggleWilling = (d) => {
    setWillingDates(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const totalShifts = periods.reduce((n, p) => n + (p.my_shifts?.length || 0), 0);

  const save = async () => {
    if (!myDate) { toast.error("Pick the shift you want to give away"); return; }
    if (willingDates.length === 0) { toast.error("Pick at least one date you're willing to work"); return; }
    setSaving(true);
    try {
      await api.post("/game/trade-ads", {
        facility_id: facilityId, my_date: myDate, willing_dates: willingDates,
      });
      toast.success("Trade ad posted");
      onSaved(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't post");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Handshake size={18} className="text-blue-600" /> Post a trade ad
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-slate-500">Loading your schedule…</div>
        ) : totalShifts === 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="ad-no-shifts">
            <div className="flex items-start gap-2">
              <Info size={16} className="mt-0.5 shrink-0" />
              <div>
                You don't have any upcoming shifts (3+ days out) at this facility to trade away. Once your schedule is published you'll be able to post a trade ad here.
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* STEP 1 — the shift I want to give away */}
            <div>
              <Label className="flex items-center gap-2 text-sm font-semibold">
                <CalendarDays size={14} className="text-blue-600" /> Shift I want to give away
              </Label>
              <div className="text-[11px] text-slate-500 mt-0.5 mb-2">
                Pick one of your upcoming shifts (must be at least 3 days out).
              </div>
              <div className="space-y-3" data-testid="ad-my-shifts">
                {periods.filter(p => p.my_shifts?.length).map(p => (
                  <div key={p.pay_period_id} className="rounded-lg border border-slate-200 bg-slate-50/60 p-2">
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{p.label}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {p.my_shifts.map(d => {
                        const selected = myDate === d;
                        return (
                          <button
                            key={d} type="button"
                            onClick={() => setMyDate(d)}
                            data-testid={`ad-my-date-${d}`}
                            className={`text-left rounded-lg border px-2.5 py-1.5 text-xs transition ${
                              selected
                                ? "border-blue-500 bg-blue-50 text-blue-900 ring-2 ring-blue-200"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            }`}>
                            <div className="font-semibold">{formatPretty(d)}</div>
                            <div className="text-[10px] text-slate-500">{d}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* STEP 2 — dates I'm willing to work */}
            {myDate && (
              <div>
                <Label className="flex items-center gap-2 text-sm font-semibold">
                  <Handshake size={14} className="text-emerald-600" /> Dates I'm willing to work
                </Label>
                <div className="text-[11px] text-slate-500 mt-0.5 mb-2">
                  Same pay period ({activePeriod?.label}). Someone scheduled on any of these can swap with you.
                </div>
                {offDaysForSelected.length === 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
                    You're already scheduled every eligible day in this pay period — nothing to offer as a return date.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid="ad-off-days">
                    {offDaysForSelected.map(d => {
                      const on = willingDates.includes(d);
                      return (
                        <label
                          key={d}
                          data-testid={`ad-off-day-${d}`}
                          className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs cursor-pointer transition ${
                            on
                              ? "border-emerald-500 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-200"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}>
                          <Checkbox
                            checked={on}
                            onCheckedChange={() => toggleWilling(d)}
                            className="h-3.5 w-3.5"
                          />
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{formatPretty(d)}</div>
                            <div className="text-[10px] text-slate-500">{d}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {willingDates.length > 0 && (
                  <div className="mt-2 text-[11px] text-slate-500" data-testid="ad-willing-count">
                    {willingDates.length} date{willingDates.length === 1 ? "" : "s"} selected
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} data-testid="ad-cancel">Cancel</Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={save}
            disabled={saving || loading || !myDate || willingDates.length === 0}
            data-testid="ad-save">
            {saving ? "Posting…" : "Create ad"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StoreTab({ items, onBuy, coins, redemptions }) {
  const active = items.filter(i => i.active);
  return (
    <div className="relative max-w-5xl mx-auto" data-testid="store-cottage">
      {/* Farm Shop cottage frame — cream siding + red awning + sign */}
      <div className="relative rounded-t-[36px] shadow-[0_18px_45px_-20px_rgba(0,0,0,0.35)] overflow-visible">
        {/* Cottage roof — brown shingles with dormer + chimney */}
        <div className="relative h-20 sm:h-24">
          <svg viewBox="0 0 600 96" preserveAspectRatio="xMidYMax meet" className="absolute inset-0 w-full h-full overflow-visible" aria-hidden="true">
            <defs>
              <linearGradient id="cottageRoof" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#7a3a20"/>
                <stop offset="1" stopColor="#4c1e10"/>
              </linearGradient>
            </defs>
            {/* Steep cottage roof silhouette */}
            <path d="M0,96 L0,64 L60,60 L300,10 L540,60 L600,64 L600,96 Z" fill="url(#cottageRoof)" stroke="#2a0f07" strokeWidth="2"/>
            {/* Roof shingle bands */}
            <path d="M20,70 L580,70 M40,80 L560,80" stroke="#2a0f07" strokeWidth="1" opacity=".5"/>
            {/* Chimney with smoke */}
            <g>
              <rect x="420" y="16" width="18" height="34" fill="#8a2d0f" stroke="#2a0f07" strokeWidth="1.5"/>
              <rect x="418" y="14" width="22" height="6" fill="#5a1a08" stroke="#2a0f07" strokeWidth="1.5"/>
              <circle cx="425" cy="8" r="3" fill="#e5e7eb" opacity=".75"/>
              <circle cx="430" cy="2" r="4" fill="#e5e7eb" opacity=".6"/>
              <circle cx="438" cy="-3" r="3.5" fill="#e5e7eb" opacity=".45"/>
            </g>
            {/* Round dormer window */}
            <circle cx="300" cy="46" r="12" fill="#f5e9d8" stroke="#2a0f07" strokeWidth="2"/>
            <line x1="288" y1="46" x2="312" y2="46" stroke="#2a0f07" strokeWidth="1.5"/>
            <line x1="300" y1="34" x2="300" y2="58" stroke="#2a0f07" strokeWidth="1.5"/>
          </svg>
        </div>

        {/* Cottage body — cream siding with dark trim */}
        <div className="relative bg-[#faf1de] border-x-[6px] border-b-[6px] border-[#5a1e0a] p-3 sm:p-4"
             style={{
               backgroundImage: "repeating-linear-gradient(0deg, rgba(90,30,10,0.08) 0 1px, transparent 1px 18px)",
             }}
        >
          {/* Hanging shop sign */}
          <div className="rounded-xl bg-[#c1441b] border-4 border-[#5a1e0a] p-3 sm:p-4 relative shadow-inner">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-white">
                <div className="font-display font-black text-xl sm:text-2xl leading-tight flex items-center gap-2">
                  <Store size={18} /> Farm Shop
                </div>
                <div className="text-[11px] sm:text-xs text-white/85 mt-0.5">
                  Spend your coins on real rewards — everything requires manager fulfilment.
                </div>
              </div>
              <span className="rounded-full bg-white/95 border border-white px-3 py-1 text-[11px] font-black text-[#4c1e10] tabular-nums inline-flex items-center gap-1">
                <Coins size={12} className="text-amber-600" /> {coins} coins
              </span>
            </div>
          </div>

          {/* Shop shelves */}
          {active.length === 0 ? (
            <div className="mt-3 rounded-xl border-2 border-dashed border-[#5a1e0a]/40 bg-white/70 p-8 text-center text-[#7a3a20] text-sm">
              The shelves are empty — ask your manager to stock the Farm Shop.
            </div>
          ) : (
            <div className="mt-3 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {active.map(it => {
                const canAfford = coins >= it.cost;
                const outOfStock = it.stock != null && it.stock <= 0;
                return (
                  <div key={it.id}
                       className="rounded-xl border-2 border-[#5a1e0a]/50 bg-white p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                       data-testid={`store-item-${it.id}`}>
                    <div className="font-display font-bold text-[#4c1e10]">{it.name}</div>
                    {it.description && <div className="text-xs text-slate-500 mt-0.5">{it.description}</div>}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center gap-1 text-amber-700 font-bold"><Coins size={13} /> {it.cost}</div>
                      {it.stock != null && <div className="text-[10px] text-slate-400">{it.stock} left</div>}
                    </div>
                    <Button size="sm" disabled={!canAfford || outOfStock} onClick={() => onBuy(it)}
                      className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`buy-${it.id}`}>
                      {outOfStock ? "Out of stock" : canAfford ? "Redeem" : `Need ${it.cost - coins} more`}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cottage foundation */}
        <div className="h-3 bg-gradient-to-b from-[#5a1e0a] to-[#3a1206] border-x-[6px] border-[#5a1e0a] rounded-b-[10px]" />
      </div>

      {/* My redemptions ledger — separate below the cottage */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="my-redemptions">
        <div className="font-display font-semibold text-sm mb-2">My redemptions</div>
        {redemptions.length === 0 ? (
          <div className="text-sm text-slate-500">You haven&apos;t redeemed anything yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {redemptions.map(r => (
              <div key={r.id} className="py-2 flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">{r.item_name}</div>
                  <div className="text-[11px] text-slate-500">{mdyTime(r.created_at)} · {r.cost} coins</div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                  r.status === "fulfilled" ? "bg-emerald-100 text-emerald-800" :
                  r.status === "approved"  ? "bg-blue-100 text-blue-800" :
                  r.status === "denied"    ? "bg-red-100 text-red-800" :
                                             "bg-slate-100 text-slate-700"
                }`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const NEW_ITEM = { name: "", description: "", cost: 6000, stock: null, active: true };

function ManageStoreTab({ facilityId, items, onChange, redemptions, onDecide }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(NEW_ITEM);
  const save = async () => {
    try {
      const payload = { ...form, facility_id: facilityId, stock: form.stock === "" ? null : form.stock };
      if (editing?.id) await api.patch(`/game/store/${editing.id}`, payload);
      else await api.post("/game/store", payload);
      toast.success("Saved"); setEditing(null); setForm(NEW_ITEM); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/game/store/${id}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  const startEdit = (it) => { setEditing(it); setForm({ ...it }); };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="space-y-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="font-display font-semibold text-sm mb-2">
            {editing?.id ? "Edit item" : "New store item"}
          </div>
          <div className="space-y-2">
            <div><Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="store-name" />
            </div>
            <div><Label className="text-xs">Description</Label>
              <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="store-desc" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Cost (coins)</Label>
                <Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: parseInt(e.target.value || "0", 10) })} data-testid="store-cost" />
              </div>
              <div><Label className="text-xs">Stock (leave blank for ∞)</Label>
                <Input type="number" value={form.stock ?? ""} onChange={(e) => setForm({ ...form, stock: e.target.value === "" ? null : parseInt(e.target.value, 10) })} data-testid="store-stock" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs pt-1">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="store-active" />
              Active (visible to employees)
            </label>
          </div>
          <div className="flex gap-2 mt-3">
            {editing && <Button variant="outline" size="sm" onClick={() => { setEditing(null); setForm(NEW_ITEM); }}>Cancel edit</Button>}
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="store-save">
              {editing ? "Update" : "Add item"}
            </Button>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">
            Suggested economy: uniform items ~6,000 coins (≈ 2–3 months of active play).
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="font-display font-semibold text-sm mb-2">Pending redemptions</div>
          {redemptions.length === 0 ? (
            <div className="text-sm text-slate-500">Nothing pending.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {redemptions.map(r => (
                <div key={r.id} className="py-2 flex items-center justify-between gap-2" data-testid={`pending-${r.id}`}>
                  <div>
                    <div className="text-sm font-medium">{r.user_name} · {r.item_name}</div>
                    <div className="text-[11px] text-slate-500">{r.cost} coins · {mdyTime(r.created_at)}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onDecide(r.id, "fulfilled")} data-testid={`fulfill-${r.id}`}>
                      <PackageCheck size={12} className="mr-1" /> Fulfilled
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onDecide(r.id, "denied")} data-testid={`deny-${r.id}`}>
                      <PackageX size={12} className="mr-1" /> Deny
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="font-display font-semibold text-sm mb-2">Current store</div>
        {items.length === 0 ? (
          <div className="text-sm text-slate-500">No items yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map(it => (
              <div key={it.id} className="py-2 flex items-center justify-between gap-2" data-testid={`manage-item-${it.id}`}>
                <div>
                  <div className="text-sm font-medium">{it.name} {!it.active && <span className="text-[10px] text-slate-400">· off</span>}</div>
                  <div className="text-[11px] text-slate-500">
                    {it.cost} coins {it.stock != null ? ` · ${it.stock} left` : " · unlimited"}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid={`edit-${it.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => remove(it.id)} className="text-red-600" data-testid={`del-${it.id}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
